import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutGrid,
  ListOrdered,
  PlayCircle,
  Wand2,
  FileCode2,
  FileText,
  Bell,
  Settings,
  LogOut,
  FileStack,
  Users,
  Globe,
  Sparkles,
  Menu,
  X,
  Link2,
  Smile,
  Lightbulb,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/dashboard", label: "Overview", icon: LayoutGrid, testid: "nav-dashboard" },
  { to: "/dashboard/prompts", label: "Prompts", icon: ListOrdered, testid: "nav-prompts" },
  { to: "/dashboard/runs", label: "Runs", icon: PlayCircle, testid: "nav-runs" },
  { to: "/dashboard/geo", label: "Geo Matrix", icon: Globe, testid: "nav-geo" },
  { to: "/dashboard/competitors", label: "Competitors", icon: Users, testid: "nav-competitors" },
  { to: "/dashboard/citations", label: "Citations", icon: Link2, testid: "nav-citations" },
  { to: "/dashboard/sentiment", label: "Sentiment", icon: Smile, testid: "nav-sentiment" },
  { to: "/dashboard/recommendations", label: "Recommendations", icon: Lightbulb, testid: "nav-recommendations" },
  { to: "/dashboard/optimizer", label: "Content Optimizer", icon: Wand2, testid: "nav-optimizer" },
  { to: "/dashboard/artefacts", label: "Artefacts", icon: FileStack, testid: "nav-artefacts" },
  { to: "/dashboard/services", label: "Services", icon: Sparkles, testid: "nav-services" },
  { to: "/dashboard/schema", label: "Schema", icon: FileCode2, testid: "nav-schema" },
  { to: "/dashboard/llms-txt", label: "llms.txt", icon: FileText, testid: "nav-llms-txt" },
  { to: "/dashboard/alerts", label: "Alerts", icon: Bell, testid: "nav-alerts" },
  { to: "/dashboard/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

function SidebarBody({ project, projects, onProjectChange, user, logout, onItemClick }) {
  return (
    <>
      <Link
        to="/dashboard"
        onClick={onItemClick}
        className="flex items-center gap-2.5 px-2 py-2"
      >
        <span className="brand-mark">A</span>
        <span style={{ fontFamily: "var(--font-body)", fontSize: 16, fontWeight: 600, letterSpacing: "-0.01em" }}>
          AnswerWatch
        </span>
      </Link>

      {projects && projects.length > 0 && (
        <div className="px-2 mt-3">
          <label className="eyebrow">Project</label>
          <select
            value={project?.project_id || ""}
            onChange={(e) => onProjectChange?.(e.target.value)}
            data-testid="project-selector"
            style={{
              marginTop: 6,
              width: "100%",
              fontSize: 13.5,
              background: "var(--aw-surface)",
              border: "1px solid var(--aw-border)",
              borderRadius: 8,
              padding: "8px 10px",
              outline: "none",
              fontFamily: "var(--font-body)",
            }}
          >
            {projects.map((p) => (
              <option key={p.project_id} value={p.project_id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
      )}

      <nav className="mt-5 space-y-0.5 overflow-y-auto">
        {NAV.map(({ to, label, icon: Icon, testid }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/dashboard"}
            data-testid={testid}
            onClick={onItemClick}
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13.5px] transition-colors ${
                isActive
                  ? "bg-[var(--aw-ink)] text-white"
                  : "text-[var(--aw-ink-2)] hover:bg-[var(--aw-surface-2)] hover:text-[var(--aw-ink)]"
              }`
            }
          >
            <Icon size={15} />
            <span>{label}</span>
          </NavLink>
        ))}
        {user?.is_admin && (
          <Link
            to="/admin"
            data-testid="nav-admin"
            onClick={onItemClick}
            className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13.5px] border border-dashed mt-3 transition-colors"
            style={{
              borderColor: "var(--aw-border-2)",
              color: "var(--aw-ink-2)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--aw-ink)";
              e.currentTarget.style.color = "white";
              e.currentTarget.style.borderColor = "var(--aw-ink)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "";
              e.currentTarget.style.color = "var(--aw-ink-2)";
              e.currentTarget.style.borderColor = "var(--aw-border-2)";
            }}
          >
            <span
              style={{
                width: 15,
                height: 15,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 4,
                background: "var(--aw-ink)",
                color: "white",
                fontFamily: "var(--font-display)",
                fontStyle: "italic",
                fontSize: 10,
              }}
            >
              A
            </span>
            <span>Admin console</span>
          </Link>
        )}
      </nav>

      <div
        className="mt-auto pt-3 px-2"
        style={{ borderTop: "1px solid var(--aw-border)" }}
      >
        <div className="flex items-center gap-2 py-2">
          {user?.picture ? (
            <img
              src={user.picture}
              alt={user.name}
              className="w-7 h-7 rounded-full"
              data-testid="user-avatar"
            />
          ) : (
            <div
              className="w-7 h-7 rounded-full"
              style={{ background: "var(--aw-surface-2)" }}
            />
          )}
          <div className="min-w-0">
            <div
              className="text-[13px] font-medium truncate"
              data-testid="user-name"
            >
              {user?.name || user?.email}
            </div>
            <div
              className="text-[11px] truncate"
              style={{ color: "var(--aw-muted)" }}
            >
              {user?.email}
            </div>
          </div>
        </div>
        <button
          onClick={logout}
          data-testid="logout-button"
          className="flex items-center gap-2 text-xs px-1 py-1.5 transition-colors"
          style={{ color: "var(--aw-muted)" }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "var(--aw-ink)")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "var(--aw-muted)")}
        >
          <LogOut size={13} /> Sign out
        </button>
      </div>
    </>
  );
}

export default function DashboardShell({ children, project, projects, onProjectChange }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div
      className="min-h-screen"
      style={{ background: "var(--aw-bg)" }}
      data-testid="dashboard-shell"
    >
      {/* Desktop sidebar */}
      <aside
        className="fixed inset-y-0 left-0 w-[240px] px-3 py-4 hidden lg:flex flex-col"
        style={{
          background: "var(--aw-surface)",
          borderRight: "1px solid var(--aw-border)",
        }}
        data-testid="sidebar"
      >
        <SidebarBody
          project={project}
          projects={projects}
          onProjectChange={onProjectChange}
          user={user}
          logout={logout}
        />
      </aside>

      {/* Mobile drawer + backdrop */}
      {mobileOpen && (
        <>
          <div
            className="fixed inset-0 z-40 lg:hidden"
            style={{
              background: "rgba(20, 18, 12, 0.32)",
              backdropFilter: "blur(2px)",
            }}
            onClick={() => setMobileOpen(false)}
            data-testid="mobile-drawer-backdrop"
          />
          <aside
            className="fixed inset-y-0 left-0 z-50 w-[260px] px-3 py-4 flex flex-col lg:hidden"
            style={{
              background: "var(--aw-surface)",
              borderRight: "1px solid var(--aw-border)",
            }}
            data-testid="mobile-drawer"
          >
            <button
              onClick={() => setMobileOpen(false)}
              data-testid="mobile-drawer-close"
              className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center transition-colors"
              style={{ color: "var(--aw-muted)" }}
              aria-label="Close menu"
            >
              <X size={18} />
            </button>
            <SidebarBody
              project={project}
              projects={projects}
              onProjectChange={onProjectChange}
              user={user}
              logout={logout}
              onItemClick={() => setMobileOpen(false)}
            />
          </aside>
        </>
      )}

      <header className="lg:pl-[240px] sticky top-0 z-30 glass-header">
        <div className="h-14 px-5 sm:px-6 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => setMobileOpen(true)}
              className="lg:hidden inline-flex items-center justify-center w-10 h-10 -ml-2"
              style={{ color: "var(--aw-ink-2)" }}
              aria-label="Open menu"
              data-testid="mobile-menu-toggle"
            >
              <Menu size={20} />
            </button>
            <button
              onClick={() => navigate("/dashboard")}
              className="lg:hidden flex items-center gap-2 min-w-0"
              data-testid="mobile-home"
            >
              <span className="brand-mark" style={{ width: 26, height: 26, fontSize: 15 }}>
                A
              </span>
              <span
                style={{
                  fontFamily: "var(--font-body)",
                  fontSize: 15,
                  fontWeight: 600,
                  letterSpacing: "-0.01em",
                }}
                className="truncate"
              >
                AnswerWatch
              </span>
            </button>
            {project && (
              <div
                className="hidden sm:flex items-center gap-2 pill"
                data-testid="current-project"
              >
                <span className="dot dot-pulse" /> {project.name}
              </div>
            )}
          </div>
          <div
            className="hidden sm:flex items-center gap-2 mono"
            style={{
              fontSize: 10.5,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "var(--aw-muted)",
            }}
          >
            <span>AnswerWatch · v2.4</span>
          </div>
        </div>
      </header>

      <main className="lg:pl-[240px]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-6 py-6 sm:py-8">{children}</div>
      </main>
    </div>
  );
}
